/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../../lib/@waca/core-client/js/core-client/ui/properties/BaseProperty',
	'../../../../lib/@waca/core-client/js/core-client/ui/properties/PropertyUIControl',
	'../../../../widgets/livewidget/nls/StringResources',
	'../api/impl/DeprecatedConditionalFormatting',
	'../../../../util/DashboardFormatter',
	'./IconPicker'
], (BaseProperty, PropertyUIControl, StringResources, DeprecatedConditionalFormatting, Formatter, IconPicker) => {

	class ConditionalFormattingProperty extends BaseProperty {
		constructor(...args) {
			super(...args);
		}

		init(options) {
			super.init(options);
			this.dashboardApi = options.dashboardApi;
			this._glassContext = options.glassContext;
			this._conditionalFormattingFeature = options.conditionalFormattingFeature;
			this._paletteColors = options.paletteColors;
			this._colorsService = this.dashboardApi.getFeature('Colors');
			this._iconPicker = options.iconPicker || this._createIconPicker();
			this._addButton = options.addButton !== undefined ? !!options.addButton : true;
		}

		doRender() {
			if (this._propertyUIControl) {
				this._propertyUIControl.remove();
			}

			this._selectedRangeIndex = this._conditionalFormattingFeature.getSelectedRangeIndex();
			this._ranges = this._conditionalFormattingFeature.getRanges();
			this._selectedRange = this._ranges[this._selectedRangeIndex];
			this._percentageRanges = this._conditionalFormattingFeature.getRangeScale() === DeprecatedConditionalFormatting.RANGE_SCALE_PERCENTAGE;
			this._currentStartValue = this._conditionalFormattingFeature.getStartValueForRange(this._selectedRangeIndex);
			this._currentEndValue = this._conditionalFormattingFeature.getEndValueForRange(this._selectedRangeIndex);

			this._propertyUIControl = new PropertyUIControl(this._getPropertySpec());
			return this._propertyUIControl.render();
		}

		createCustomColor(hex) {
			const className = this._colorsService.addCustomColor(hex);
			const colorProp = this._colorsService.createColorDefinition(className, hex, hex);
			colorProp.type = 'ColorCode';
			colorProp.hidden = true;
			return colorProp;
		}

		_createIconPicker() {
			const container = document.body;
			const shapeNameList = this._conditionalFormattingFeature.getShapeList();
			const icons = shapeNameList.map((shapeName) => {
				let shapeDef = this._conditionalFormattingFeature.getShapeDefinition(shapeName);
				return { id: shapeDef.id, label: shapeDef.label, name: shapeName };
			});
			const properties = {
				icons: icons,
				onIconSelected: this._handleIconSelect.bind(this),
				containerNode: container
			};
			return new IconPicker(properties);
		}

		_getPropertySpec() {
			const iconsFeature  = this.dashboardApi.getFeature('Icons');
			const trashIcon = iconsFeature.getIcon('TrashIcon');
			const restoreIcon = iconsFeature.getIcon('RestoreIcon');
			const reverseIcon = iconsFeature.getIcon('ReverseIcon');

			const deleteSelectedStepAction = {
				type: 'icon',
				svgIcon: trashIcon.id,
				iconTooltip: StringResources.get('conditionalFormattingDeleteStep'),
				class: 'deleteSelectedStepAction'
			};

			if (this._ranges.length > 1) {
				deleteSelectedStepAction.clickCallback = () => {
					this._conditionalFormattingFeature.removeRange(this._selectedRangeIndex);
				};
			} else {
				deleteSelectedStepAction.disabled = true;
				deleteSelectedStepAction.class += ' stepActionDisabled';
			}

			const childPropertyItems = [{
				type: 'SingleLineLinks',
				id: 'selectedSteps',
				name: 'selectedSteps',
				items: [{
					align: 'left',
					items: [{
						type: 'text',
						class: 'boldPropertyText',
						value: StringResources.get('conditionalFormattingSelectedStep')
					}]
				}, {
					align: 'right',
					items: [deleteSelectedStepAction, {
						type: 'icon',
						svgIcon: restoreIcon.id,
						iconTooltip: StringResources.get('conditionalFormattingRestoreSteps'),
						class: 'restoreStepsAction',
						clickCallback: () => {
							this._conditionalFormattingFeature.resetRanges();
						}
					}, {
						type: 'icon',
						svgIcon: reverseIcon.id,
						iconTooltip: StringResources.get('conditionalFormattingReversePalette'),
						class: 'reversePaletteAction',
						clickCallback: () => {
							this._conditionalFormattingFeature.reverseColors();
						}
					}]
				}]
			}, {
				id: 'conditionalColorRange',
				module: 'dashboard-analytics/features/widget/conditionalFormatting/properties/ColourRange',
				properties: {
					onSelectCallback: (selectedRange) => {
						this._conditionalFormattingFeature.setSelectedRangeIndex(selectedRange.rangeIndex);
						this.doRender();
					},
					items: this._buildColorRangePayload()
				}
			}, {
				id: 'startValue',
				name: 'startValue',
				type: 'InputLabel',
				label: StringResources.get('conditionalFormattingStartValue'),
				value: this._getFormattedStartValue(),
				handleReturnKey: true,
				decimalPlaces: 2,
				placeHolderText: this._selectedRangeIndex === 0 ? StringResources.get('conditionalFormattingMin') : '',
				newLook: true,
				ellipses: true,
				inputStyles: this._percentageRanges ? 'width: 80px' : '',
				showInlineError: true,
				customValidatorCallback: (startValue) => {
					return this._conditionalFormattingFeature.checkValidStartValueForRange(this._getNumberFromFormattedValue(startValue), this._selectedRangeIndex);
				},
				onChange: (propertyName, startValue) => {
					const value = this._getNumberFromFormattedValue(startValue);
					if (value === this._currentStartValue) {
						// If the value hasn't changed, make sure the input control is still showing the formatted value
						this._propertyUIControl.getProperty('startValue').setValue(this._getFormattedStartValue());
					} else {
						this._conditionalFormattingFeature.setStartValueForRange(value, this._selectedRangeIndex);
					}
				}
			}, {
				id: 'endValue',
				name: 'endValue',
				type: 'InputLabel',
				label: StringResources.get('conditionalFormattingEndValue'),
				value: this._getFormattedEndValue(),
				handleReturnKey: true,
				decimalPlaces: 2,
				placeHolderText: this._selectedRangeIndex === (this._ranges.length - 1) ? StringResources.get('conditionalFormattingMax') : '',
				newLook: true,
				ellipses: true,
				inputStyles: this._percentageRanges ? 'width: 80px' : '',
				showInlineError: true,
				customValidatorCallback: (endValue) => {
					return this._conditionalFormattingFeature.checkValidEndValueForRange(this._getNumberFromFormattedValue(endValue), this._selectedRangeIndex);
				},
				onChange: (propertyName, endValue) => {
					const value = this._getNumberFromFormattedValue(endValue);
					if (value === this._currentEndValue) {
						// If the value hasn't changed, make sure the input control is still showing the formatted value
						this._propertyUIControl.getProperty('endValue').setValue(this._getFormattedEndValue());
					} else {
						this._conditionalFormattingFeature.setEndValueForRange(value, this._selectedRangeIndex);
					}
				}
			}, {
				id: 'rangeColor',
				name: 'rangeColor',
				type: 'ColorPicker',
				label: StringResources.get('conditionalFormattingTextColor'),
				open: false,
				ariaLabel: StringResources.get('conditionalFormattingTextColor'),
				paletteType: 'ConditionalPalette',
				showHexValue: true,
				onChange: (propertyName, valueInfo) => {
					this._conditionalFormattingFeature.setColorIdForRange(valueInfo.name, this._selectedRangeIndex);
				},
				createCustomColor: this.createCustomColor.bind(this),
				items: this._getColorPickerItems(),
				addButton: this._addButton,
				colorClass: true,
				selectedName: this._conditionalFormattingFeature.getColorIdForRange(this._selectedRangeIndex)
			}];
			childPropertyItems.push(this._createIconPickerPropertySpec());

			return {
				el: this.$el,
				glassContext: this._glassContext,
				primaryUIControl: false,
				items: childPropertyItems
			};
		}

		_createIconPickerPropertySpec() {
			const shape = this._conditionalFormattingFeature.getShapeForRange(this._selectedRangeIndex);
			const iconDef = this._conditionalFormattingFeature.getShapeDefinition(shape);
			return {
				id: 'conditionalFormattingShape',
				name: 'conditionalFormattingShape',
				type: 'SingleLineLinks',
				items: [{
					align: 'left',
					items: [{
						type: 'text',
						id: 'conditionalFormattingShapeText',
						value: StringResources.get('conditionalFormattingShapeIndicator')
					}]
				}, {
					align: 'right',
					items: [{
						type: 'icon',
						id: 'conditionalFormattingShapeIcon',
						name: 'conditionalFormattingShapeIcon',
						svgIcon: iconDef.id,
						iconTooltip: iconDef.label,
						clickCallback: (evt) => {
							this._toggleShapeIndicator(evt);
						}
					}]
				}]
			};
		}

		_toggleShapeIndicator(evt) {
			if (this._iconPicker.isOpen()) {
				this._iconPicker.close();
				return;
			}

			this._iconPicker.open(evt.currentTarget);
		}

		_handleIconSelect(icon) {
			this._conditionalFormattingFeature.setShapeForRange(icon.name, this._selectedRangeIndex);
		}

		_getFormattedStartValue() {
			return this._formatValueForInput(this._currentStartValue);
		}

		_getFormattedEndValue() {
			return this._formatValueForInput(this._currentEndValue);
		}

		_getColorPickerItems() {
			let propertyItems = [];
			if (!this._paletteColors) {
				return [];
			}

			this._paletteColors.forEach((color) => {
				propertyItems.push({
					type: 'ColorCode',
					value: color.hexValue,
					name: color.name,
					label: color.label
				});
			});
			let ranges = this._conditionalFormattingFeature.getRanges();
			let range = ranges[this._selectedRangeIndex];
			if(range && range.userSetColorId && this._colorsService.isCustomColor(range.userSetColorId)) {
				let hexValue = this._colorsService.getHexColorFromClassName(range.userSetColorId);
				propertyItems.push({
					type: 'ColorCode',
					value: hexValue,
					name: range.userSetColorId,
					label: hexValue,
					hidden: true
				});
			}

			return propertyItems;
		}

		_getNumberFromFormattedValue(value) {
			let stringValue = value + '';
			if (stringValue.endsWith('%')) {
				stringValue = stringValue.substring(0, stringValue.length - 1);
			}

			if (value === '') {
				return null;
			}

			return Formatter.getNumberFromFormattedValue(stringValue);
		}

		_formatValueForInput(value) {
			if (value === null) {
				return '';
			}
			let number = Formatter.format(value, {
				maximumFractionDigits: 2,
				type: 'number'
			});

			if (this._percentageRanges) {
				number += '%';
			}

			return number;
		}

		_formatValueForRangeControlLabel(value) {
			const number = Formatter.format(value, {
				maximumFractionDigits: 2,
				decimalFormatLength: 'short',
				type: 'number'
			});

			return number;
		}

		_buildColorRangePayload() {
			let items = [];
			this._ranges.forEach((range, index) => {
				items.push({
					start_label: range.startValue === null ? StringResources.get('conditionalFormattingMin') : this._formatValueForRangeControlLabel(range.startValue),
					end_label: range.endValue === null ? StringResources.get('conditionalFormattingMax') : this._formatValueForRangeControlLabel(range.endValue),
					color: this._conditionalFormattingFeature.getColorHexForRange(index),
					rangeIndex: index
				});
			});
			items[this._selectedRangeIndex].selected = true;
			return items;
		}
	}


	return ConditionalFormattingProperty;
});
